import { useState } from "react";
import { toast } from "sonner";
import { CheckSquare, X } from "lucide-react";
import { setRecordType } from "@/lib/api";
import { Chip } from "@/components/brand/AppHeader";

export const RECORD_TYPES = [
  ["horse_sale", "Horse Sales"], ["review", "Needs Review"], ["prospect", "Prospects / Leads"],
  ["notes", "Notes / Other"], ["archived", "Archived"], ["all", "All Records"],
];
const MOVE = [["horse_sale", "Horse Sale"], ["prospect", "Prospect / Lead"], ["notes", "Notes / Other"], ["archived", "Archived"]];
export const typeOf = (h) => h.record_type || "horse_sale";

export function RecordTypeTabs({ value, onChange, counts, className = "mt-3" }) {
  return (
    <div data-testid="record-type-filter" role="tablist" className={`${className} -mx-1 px-1 flex gap-1.5 overflow-x-auto hide-scrollbar`}>
      {RECORD_TYPES.map(([k, l]) => (
        <Chip key={k} role="tab" active={value === k} testId={`record-type-${k}`} onClick={() => onChange(k)} count={counts[k] ?? 0}>{l}</Chip>
      ))}
    </div>
  );
}

// Multi-select + "Move to…" bar. Classification only; nothing is deleted.
export function useSelection() {
  const [on, setOn] = useState(false);
  const [ids, setIds] = useState(() => new Set());
  const toggle = (id) => setIds((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const reset = () => { setIds(new Set()); setOn(false); };
  return { on, setOn, ids, setIds, toggle, reset };
}

export function SelectToggle({ sel }) {
  return (
    <button type="button" data-testid="select-mode-toggle" onClick={() => (sel.on ? sel.reset() : sel.setOn(true))}
      className="inline-flex items-center gap-1 min-h-[44px] lg:min-h-0 px-2 text-stone-700 font-semibold hover:underline">
      {sel.on ? <><X className="h-3.5 w-3.5" />Cancel selection</> : <><CheckSquare className="h-3.5 w-3.5" />Select records</>}
    </button>
  );
}

export function BulkBar({ sel, visible, onDone }) {
  const [busy, setBusy] = useState(false);
  if (!sel.on) return null;
  const n = sel.ids.size;
  const move = async (t, l) => {
    setBusy(true);
    try { const r = await setRecordType([...sel.ids], t); toast.success(`${r.updated} record${r.updated === 1 ? "" : "s"} moved to ${l}`); sel.reset(); onDone(); }
    catch { toast.error("Could not update records"); } finally { setBusy(false); }
  };
  return (
    <div data-testid="bulk-bar" className="fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] lg:bottom-4 z-40 px-3">
      <div className="on-dark max-w-3xl mx-auto rounded-2xl border border-white/10 shadow-2xl p-3 flex flex-wrap items-center gap-2">
        <span data-testid="bulk-count" className="text-sm font-semibold mr-1 text-white">{n} selected</span>
        <button type="button" data-testid="bulk-select-all" onClick={() => sel.setIds(new Set(visible.map((h) => h.id)))} className="h-9 px-3 rounded-full border border-white/25 text-white text-xs font-semibold">Select all shown ({visible.length})</button>
        <span className="text-xs text-white/75 ml-auto">Move to:</span>
        {MOVE.map(([t, l]) => (
          <button key={t} type="button" data-testid={`bulk-move-${t}`} disabled={!n || busy} onClick={() => move(t, l)}
            className="h-9 px-3 rounded-full bg-stone-600 hover:bg-stone-500 text-white disabled:bg-stone-200 disabled:text-stone-500 text-xs font-semibold">{l}</button>
        ))}
      </div>
    </div>
  );
}

export function Selectable({ sel, h, children }) {
  if (!sel.on) return children;
  const checked = sel.ids.has(h.id);
  return (
    <div data-testid={`select-row-${h.id}`} className={`relative ${checked ? "ring-2 ring-ink rounded-2xl" : ""}`}>
      <button type="button" role="checkbox" aria-checked={checked} aria-label={`Select ${(h.horse_info || {}).horse_name || "record"}`}
        data-testid={`select-check-${h.id}`} onClick={() => sel.toggle(h.id)} className="absolute inset-0 z-10 flex items-start justify-end p-2">
        <span className={`h-7 w-7 rounded-md border-2 flex items-center justify-center text-sm font-bold ${checked ? "bg-amber-700 border-amber-700 text-white" : "bg-white border-stone-400"}`}>{checked ? "✓" : ""}</span>
      </button>
      {h.smartsheet_row_number && <span className="absolute left-2 bottom-1 z-10 rounded bg-stone-900/80 text-white text-[10px] font-semibold px-1.5 py-0.5">Row {h.smartsheet_row_number}</span>}
      {children}
    </div>
  );
}
